import type { EditorConfig } from '@ckeditor/ckeditor5-core/src/editor/editorconfig';
import { CKEditor } from '@ckeditor/ckeditor5-react';

import { uploadFile } from '@/data/services/fileService';
import {
  default as Editor,
  default as MyEditor,
} from 'ckeditor5-custom-build/build/ckeditor';
import { decamelize } from 'humps';
import { CSSProperties, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageFolderEnum } from '@/models/CkEditor';
import Skeleton from '@/components/ui/custom/Skeleton';
import {
  DisplayFormat,
  formatClasses,
} from '@/components/ui/custom/dataDisplay/HtmlPreview';
import { mountAudioPlayer } from '@/components/ui/custom/AudioPlayer';
import { isSafeAudioSrc } from '@/utils/validators/isSafeAudioSrc';
import {
  IframeAllowlistEntry,
  isAllowedIframeSrc,
} from '@/utils/validators/isAllowedIframeSrc';
import { useIframeAllowlist } from '@/data/hook/useIframeAllowlist';
import useStableValue from '@/data/hook/shared/useStableValue';
import alert from '@/utils/shared/alert';

interface CkeditorProps {
  content: string;
  folder: ImageFolderEnum;
  placeholder?: string;
  handleContentChange: (value: string) => void;
  onUploadStart?: (image: string | Blob) => void;
  onUploadEnd?: () => void;
  onBlur?: () => void;
  testId?: string;
  format?: DisplayFormat;
  disabled?: boolean;
  isLoading?: boolean;
  imageUploadBody?: object;
  onEditorReady?: (editor: typeof Editor) => void;
}

function MyCkeditor(props: CkeditorProps) {
  const {
    handleContentChange,
    folder,
    content,
    onBlur,
    onEditorReady,
    format,
    placeholder,
    testId,
    disabled,
    isLoading,
    imageUploadBody,
    onUploadEnd,
    onUploadStart,
  } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'ckeditor',
  });

  const rawAllowlist = useIframeAllowlist();
  const iframeAllowlist = useStableValue(rawAllowlist);
  const warnedIframeHostsRef = useRef<Set<string>>(new Set());

  const config: EditorConfig = {
    heading,
    licenseKey: 'GPL',
    toolbar: {
      shouldNotGroupWhenFull: true,
      items: [
        'findAndReplace',
        '|',
        'heading',
        '|',
        {
          label: 'Font',
          icon: 'paragraph',
          items: [
            'fontfamily',
            'fontsize',
            'fontColor',
            'fontBackgroundColor',
            'highlight',
          ],
        },
        '|',
        'bold',
        'italic',
        'underline',
        '|',
        'code',
        'codeBlock',
        '|',
        {
          label: 'List',
          icon: false,
          items: ['bulletedList', 'numberedList', 'todoList'],
        },
        '|',
        'mediaEmbed',
        'uploadImage',
        'audioUpload',
        'link',
        format === 'book' ? 'htmlEmbed' : '',
        '|',
        'outdent',
        'indent',
        '|',
        'alignment',
        '|',
        'insertTable',
        'undo',
        'redo',
      ],
    },
    extraPlugins: [UploadPlugin, ClipboardPlugin],
    placeholder: placeholder || t('placeholder'),
    audio: {
      isSafeSrc: isSafeAudioSrc,
      upload: async (file: File) => {
        onUploadStart?.(file);
        try {
          const uploaded = await uploadFile({
            file,
            folder,
            extraBody: imageUploadBody,
          });
          return uploaded.image;
        } finally {
          onUploadEnd?.();
        }
      },
      renderPlayer: mountAudioPlayer,
    },
    mediaEmbed: {
      providers: [
        {
          name: 'loom',
          url: /^loom\.com\/share\/(\w+)/,
          html: (match: string[]) => {
            const id = match[1];
            return `
                <div style="position: relative; padding-bottom: 62.5%; height: 0;">
                  <iframe 
                    src="https://www.loom.com/embed/${id}?sid=40770b83-7e2e-4699-a8a5-587c8f283549" 
                    frameborder="0" 
                    webkitallowfullscreen 
                    mozallowfullscreen 
                    allowfullscreen 
                    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;">
                  </iframe>
                </div>
              `;
          },
        },
      ],
    },
  } as EditorConfig;

  const uploadAdapterRef = useRef<MyUploadAdapter | null>(null);

  type ClipboardData = {
    dataTransfer: DataTransfer;
    content: DocumentFragment;
  };

  function handleTextPate(
    editor: typeof Editor,
    _: Event,
    data: ClipboardData,
  ) {
    const dataTransfer = data.dataTransfer;
    let htmlContent = dataTransfer.getData('text/html');

    if (!htmlContent) {
      return;
    }

    htmlContent = removeCSSFromInlineStyle(htmlContent, [
      'color',
      'backgroundColor',
    ]);

    const { html: cleaned, blockedHosts } = stripDisallowedIframes(
      htmlContent,
      iframeAllowlist,
    );
    const newHosts = Array.from(blockedHosts).filter(
      host => !warnedIframeHostsRef.current.has(host),
    );
    newHosts.forEach(host => warnedIframeHostsRef.current.add(host));
    newHosts.forEach(host => alert.warning(t('iframeBlocked', { host })));

    const viewFragment = editor.data.processor.toView(cleaned);
    data.content = viewFragment;
  }

  function handleEditorReady(editor: typeof Editor) {
    onEditorReady?.(editor);
    editor.editing.view.document.on('blur', () => {
      const html = editor.getData();
      if (!html.includes('<iframe')) return;

      const { html: cleaned, blockedHosts } = stripDisallowedIframes(
        html,
        iframeAllowlist,
      );
      if (blockedHosts.size === 0) return;

      const newHosts = Array.from(blockedHosts).filter(
        host => !warnedIframeHostsRef.current.has(host),
      );
      newHosts.forEach(host => warnedIframeHostsRef.current.add(host));

      editor.setData(cleaned);
      newHosts.forEach(host => alert.warning(t('iframeBlocked', { host })));
    });
  }

  //TODO: voltar a tipagem original do ckeditor5-custom-build/build/ckeditor ./typing.d.ts
  function ClipboardPlugin(editor: typeof Editor) {
    editor.editing.view.document.on(
      'clipboardInput',
      (_: Event, data: ClipboardData) => handleTextPate(editor, _, data),
    );
  }

  // Atualiza o uploadAdapterRef quando a prop imageUploadBody é alterada
  useEffect(() => {
    if (uploadAdapterRef.current) {
      uploadAdapterRef.current.imageUploadBody = imageUploadBody || {};
    }
  }, [imageUploadBody]);

  class MyUploadAdapter {
    loader;
    imageUploadBody?: object;
    constructor(loader: { file: string | Blob }) {
      this.loader = loader;
      this.imageUploadBody = imageUploadBody || {};
    }

    upload() {
      onUploadStart?.(this.loader.file);
      const promise = Promise.resolve(this.loader.file).then(res => res);
      return promise.then(async (file: string | Blob) => {
        const imageUploaded = await uploadFile({
          file,
          folder,
          extraBody: this.imageUploadBody,
        });
        onUploadEnd?.();
        return {
          default: imageUploaded.image,
        };
      });
    }

    abort() {}
  }

  function UploadPlugin(editor: {
    plugins: {
      get: (repository: string) => {
        createUploadAdapter: (loader: { file: string | Blob }) => void;
      };
    };
  }) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: {
      file: string | Blob;
    }) => {
      const adapter = new MyUploadAdapter(loader);
      uploadAdapterRef.current = adapter;
      return uploadAdapterRef.current;
    };
  }

  const removeCSSFromInlineStyle = (
    htmlString: string,
    ignoreCSSProperties: (keyof CSSProperties)[],
  ) => {
    let textoFormatado = htmlString;

    ignoreCSSProperties.forEach(property => {
      const regex = new RegExp(
        `${decamelize(property).replace('_', '-')}:[^;]+;`,
        'g',
      );
      textoFormatado = textoFormatado.replace(regex, '');
    });

    return textoFormatado;
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-1">
        <Skeleton className="bg-primary w-full h-9 rounded-lg" />
        <Skeleton className="bg-primary w-full h-24 rounded-lg" />
      </div>
    );
  }

  return (
    <div className={`${formatClasses[format || 'default']} text-base-content`}>
      <CKEditor
        id="CkEditor"
        config={config}
        editor={MyEditor}
        data-testid={testId}
        data={content}
        disabled={!!disabled}
        onReady={handleEditorReady}
        onChange={(_, editor: { getData: () => string }) => {
          const data = editor.getData();
          handleContentChange(data);
        }}
        onBlur={(_event: object) => {
          if (onBlur) {
            onBlur();
          }
        }}
      />
    </div>
  );
}

function stripDisallowedIframes(
  html: string,
  allowlist: IframeAllowlistEntry[],
): { html: string; blockedHosts: Set<string> } {
  const blockedHosts = new Set<string>();
  if (!html.includes('<iframe')) return { html, blockedHosts };

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const iframes = Array.from(doc.body.querySelectorAll('iframe'));
  if (iframes.length === 0) return { html, blockedHosts };

  iframes.forEach(iframe => {
    const src = iframe.getAttribute('src') ?? '';
    if (isAllowedIframeSrc(src, allowlist)) return;
    let host = '';
    try {
      host = src ? new URL(src).hostname : '';
    } catch {
      host = '';
    }
    blockedHosts.add(host || src || 'iframe');
    iframe.remove();
  });

  if (blockedHosts.size === 0) return { html, blockedHosts };
  return { html: doc.body.innerHTML, blockedHosts };
}

const heading = {
  options: [
    {
      model: 'paragraph',
      title: 'Paragraph',
      class: 'ck-heading_paragraph',
    },
    {
      model: 'heading1',
      view: 'h1',
      title: 'Heading 1',
      class: 'ck-heading_heading1',
    },
    {
      model: 'heading2',
      view: 'h2',
      title: 'Heading 2',
      class: 'ck-heading_heading2',
    },
    {
      model: 'heading3',
      view: 'h3',
      title: 'Heading 3',
      class: 'ck-heading_heading2',
    },
  ],
};

export default MyCkeditor;
